import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {ViewProps} from './model';
import {useStyles} from '../../../../../shared/hooks/styles';
import {LoadableContent} from '../../../../../shared/components/loadableContent';
import {DeviceSize} from '../../../../../shared/theme/helpers';
import {formFields, formFieldsNames, passportFormFieldNames} from './constants';
import {Field} from './components/field';
import {Button} from '../../../../../shared/components/button';
import {Text} from '../../../../../shared/components/text';
import {Checkbox} from '../../../../../shared/components/baseField/components/checkbox/checkbox';
import {CustomScrollView} from '../../../../../shared/components/scrollView';
import {Form} from '../../../../../shared/components/form';
import {ExternalLink} from '../../../../../shared/components/externalLink';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {createKidSubformWithDynamicName} from './kidSubform';
import {useSelector} from 'react-redux';
import {holderProfileSelector} from '../../../../../redux/selectors/profile';
import {useIsSmallScreenDevice} from '../../../../../shared/hooks/useIsSmallScreenDevice';

export default ({
  formValues,
  isAccepted,
  onAcceptedToggle,
  loading,
  onSubmit,
  submitting,
  submissionErrors,
  setSubmissionErrors,
  readOnly,
}: ViewProps) => {
  const isSmallScreen = useIsSmallScreenDevice();
  const styles = useStyles(({theme, mediaOnly, isDesktop, platformOnly}) => {
    return {
      form: {
        marginTop: 14,
        marginHorizontal: 10,
        ...mediaOnly(DeviceSize.desktop, {
          paddingLeft: 60,
          paddingRight: 60,
          paddingTop: 50,
          paddingBottom: 30,
        }),
      },
      kidForm: {
        marginHorizontal: 0,
        ...mediaOnly(DeviceSize.desktop, {
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }),
      },
      formSubmitBox: {
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 10,
      },
      submit: StyleSheet.flatten([{width: 340}, mediaOnly(DeviceSize.desktop, {marginBottom: 16}, {marginBottom: 24})]),
      checkbox: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ...mediaOnly(DeviceSize.desktop, {width: '100%'}),
        ...mediaOnly(DeviceSize.tablet, {width: 560}),
        ...mediaOnly(DeviceSize.mobile, {width: 260}),
      },
      formCheckbox: {
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        marginBottom: 10,
        '&:hover': {
          cursor: 'pointer',
        },
      },
      checkboxBtn: {
        marginRight: 12,
      },
      agreement: {
        ...mediaOnly(DeviceSize.desktop, {}, {flex: 1}),
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
      },
      agreementText: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, {...theme.fonts.bodySmallLight, lineHeight: 24}),
      },
      agreementTextDoc: {
        color: theme.color.primaryColor,
      },
      notificationSendAgreementText: {
        fontSize: isDesktop ? 18 : 14,
        ...platformOnly('web', {width: '100%'}, {width: '80%'}),
        marginRight: isSmallScreen ? 10 : 0,
      },
      childrenFieldName: {
        fontSize: 16,
        color: 'rgb(130, 130, 130)',
        ...platformOnly('web', {width: '100%'}, {width: '80%'}),
      },
      kidsTableCheckboxIcon: {
        height: 24,
        width: 24,
        color: theme.color.primaryColor,
        backgroundColor: 'white',
      },
      colIcon: {
        flexBasis: 80,
        alignItems: 'flex-start',
        justifyContent: 'center',
      },
      colSnils: {
        flexBasis: 150,
        alignItems: 'flex-start',
        justifyContent: 'center',
      },
      colBirthday: {
        flexBasis: 110,
      },
      kidsLabel: {
        ...theme.fonts.h3,
        alignSelf: 'center',
        marginBottom: 14,
      },
      kidFormBlock: {
        borderBottomColor: 'black',
        borderBottomWidth: 2,
        marginBottom: 20,
      },
      gurdianText: {
        marginTop: 0,
        marginLeft: 20,
        ...theme.fonts.h3,
        alignSelf: 'center',
        textAlign: 'center',
      },
    };
  });
  const profile = useSelector(holderProfileSelector());
  const isLogged = !!profile;
  const {checkedKids, parent} = useRegCardState();

  return (
    <LoadableContent loading={loading} style={{height: '80%'}}>
      {formValues && !loading ? (
        <CustomScrollView withRefresh={false} contentInsetAdjustmentBehavior='automatic'>
          {isLogged ? null : (
            <View style={{display: 'flex'}}>
              <Text style={styles.gurdianText}>Данные заявителя</Text>
            </View>
          )}
          <Form onSubmit={onSubmit} style={styles.form}>
            <View testID='esia-reg-card-form'>
              {formFieldsNames.map((name) => {
                const config = formFields[name];
                if (!config) return null;
                if (readOnly && (passportFormFieldNames.includes(name) || config.type === 'checkbox')) return null;
                const fieldProps = {
                  ...config,
                  editable: readOnly ? false : config.editable,
                  disabled: readOnly,
                };
                if (config.type === 'checkbox' && config.label) {
                  if (
                    config.label === 'Привязать детские аккаунты к родительскому' &&
                    (checkedKids.length === 0 || (parent?.checked === false && !isLogged))
                  ) {
                    return null;
                  }
                  return (
                    <View
                      style={[
                        styles.formCheckbox,
                        {marginTop: name === 'isEmailNotificationEnabled' ? 16 : 0},
                        {marginLeft: 20},
                      ]}
                      key={'esia-reg-card-form-checkbox' + name}>
                      {Platform.OS !== 'web' && (
                        <Text style={styles.notificationSendAgreementText}>{config.label}</Text>
                      )}
                      <Field
                        error={submissionErrors?.[name]?.error ?? null}
                        setSubmissionErrors={setSubmissionErrors}
                        key={name}
                        value={formValues[name]}
                        name={name}
                        {...fieldProps}
                        label={''}
                      />
                      {Platform.OS === 'web' && (
                        <Text style={[styles.notificationSendAgreementText]}>{config.label}</Text>
                      )}
                    </View>
                  );
                }
                if (config.label === 'Электронная почта') {
                  return (
                    <React.Fragment key={name}>
                      <Field
                        error={submissionErrors?.[name]?.error ?? null}
                        setSubmissionErrors={setSubmissionErrors}
                        value={formValues[name]}
                        name={name}
                        {...fieldProps}
                      />
                      {checkedKids.length ? (
                        <>
                          <View>
                            <Text style={styles.kidsLabel}>Дети заявителя:</Text>
                          </View>
                          {checkedKids.map((kid, checkedKidIndex) => {
                            const KidSubformWithDynamicName = createKidSubformWithDynamicName(checkedKidIndex);
                            return (
                              <View key={checkedKidIndex} style={styles.kidFormBlock}>
                                <KidSubformWithDynamicName kidId={checkedKidIndex} />
                              </View>
                            );
                          })}
                        </>
                      ) : null}
                    </React.Fragment>
                  );
                }
                return (
                  <Field
                    error={submissionErrors?.[name]?.error ?? null}
                    setSubmissionErrors={setSubmissionErrors}
                    key={name}
                    value={formValues[name]}
                    name={name}
                    {...fieldProps}
                  />
                );
              })}
            </View>

            <View style={styles.formSubmitBox}>
              <Button
                style={styles.submit}
                disabled={(!readOnly && !isAccepted) || submitting}
                onPress={() => {
                  onSubmit();
                }}
                text='Перейти к способу получения'
                id='esia-reg-card-form-submit'
              />
              {readOnly ? null : (
                <View style={styles.checkbox}>
                  <Checkbox
                    style={styles.checkboxBtn}
                    testIDprefix='esia-reg-card-form-submit'
                    value={isAccepted}
                    onChange={onAcceptedToggle}
                  />
                  <View style={styles.agreement}>
                    <Text style={styles.agreementText}>Я принимаю</Text>
                    <ExternalLink
                      testID='agreeemnet-external-id'
                      style={[styles.agreementText, styles.agreementTextDoc]}
                      link={
                        /*content.docs.agreement*/ 'https://cdn.eskso.ru/documents/Consent_to_processing_PDn-ESIA.pdf'
                      }
                      text=' соглашение '
                      showLoadingText={false}
                      fileName={'eskso_agreement'}
                    />
                    <Text style={styles.agreementText}>об использовании персональных данных</Text>
                  </View>
                </View>
              )}
            </View>
          </Form>
        </CustomScrollView>
      ) : null}
    </LoadableContent>
  );
};
